import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Component, LOCALE_ID, PLATFORM_ID, OnInit, inject } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { filter, firstValueFrom, timer } from 'rxjs';
import dayjs from 'dayjs';

import { Router, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { JwtHelperService } from '@auth0/angular-jwt';

import * as SkiworldModels from '@app/models/skiworld-models';
import { DataService } from '@app/services/data.service';
import { AuthService } from '@app/auth.service';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-ribbon',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatDividerModule, MatMenuModule, MatIconModule, MatTooltipModule, TranslateModule, MatToolbarModule, MatButtonModule, RouterModule],
  templateUrl: './ribbon.component.html',
  styleUrls: ['./ribbon.component.scss'],
})
export class RibbonComponent implements OnInit {
  locale = inject(LOCALE_ID);
  private platformId = inject(PLATFORM_ID);
  private auth = inject(AuthService) as AuthService;
  private jwtHelper = inject(JwtHelperService);
  private translate = inject(TranslateService);
  private lang = inject(LanguageService);
  private dataService = inject(DataService) as DataService;
  private router = inject(Router);
  private fb = inject(FormBuilder);
  duration: SkiworldModels.Duration = {} as SkiworldModels.Duration;

  public translateCode: SkiworldModels.LangCode | string = 'de';
  public localeCode: string = 'de';

  headerClass = 'small';
  isAuthenticated = false;
  username: string | null = null;
  password = '';
  email: string | null = null;

  decodedToken: Record<string, unknown> | null = null; // Replace with a more specific type if known
  currentUser: SkiworldModels.User = {} as SkiworldModels.User;

  public loginForm: FormGroup = {} as FormGroup;
  public logoutForm: FormGroup = {} as FormGroup;
  public locationName = '-';
  public rentDuration = '';
  public rentDurationStart = '';
  public rentDurationEnd = '';
  public rentDurationDayCount = 0;
  public personCount = 0;
  public adultsCount = 0;
  public juniorsCount = 0;
  public childrenCount = 0;
  public products = 0;
  public onlineAmount = 0;
  public storeAmount = 0;
  private persons: SkiworldModels.Person[] = [];

  public localeSelector: SkiworldModels.LangCode | null = 'de';

  public languages: ReadonlyArray<SkiworldModels.LanguageOption> = [
    { code: 'de', labelKey: 'LANGUAGES.GERMAN', flagClass: 'iti__de' },
    { code: 'en', labelKey: 'LANGUAGES.ENGLISH', flagClass: 'iti__gb' },
    //
    { code: 'ae', labelKey: 'LANGUAGES.ARABIC', flagClass: 'iti__ae' },
    { code: 'cs', labelKey: 'LANGUAGES.CZECH', flagClass: 'iti__cz' },
    { code: 'da', labelKey: 'LANGUAGES.DANISH', flagClass: 'iti__dk' },
    { code: 'es', labelKey: 'LANGUAGES.SPANISH', flagClass: 'iti__es' },
    { code: 'fi', labelKey: 'LANGUAGES.FINNISH', flagClass: 'iti__fi' },
    { code: 'fr', labelKey: 'LANGUAGES.FRENCH', flagClass: 'iti__fr' },
    { code: 'hr', labelKey: 'LANGUAGES.CROATIAN', flagClass: 'iti__hr' },
    { code: 'hu', labelKey: 'LANGUAGES.HUNGERIAN', flagClass: 'iti__hu' },
    { code: 'il', labelKey: 'LANGUAGES.HEBREW', flagClass: 'iti__il' },
    { code: 'it', labelKey: 'LANGUAGES.ITALIAN', flagClass: 'iti__it' },
    { code: 'nl', labelKey: 'LANGUAGES.DUTCH', flagClass: 'iti__nl' },
    { code: 'no', labelKey: 'LANGUAGES.NORWEGIAN', flagClass: 'iti__no' },
    { code: 'pl', labelKey: 'LANGUAGES.POLISH', flagClass: 'iti__pl' },
    { code: 'ru', labelKey: 'LANGUAGES.RUSSIAN', flagClass: 'iti__ru' },
    { code: 'se', labelKey: 'LANGUAGES.SWEDISH', flagClass: 'iti__se' },
    { code: 'si', labelKey: 'LANGUAGES.SLOVENIAN', flagClass: 'iti__si' },
    { code: 'sk', labelKey: 'LANGUAGES.SLOVAK', flagClass: 'iti__sk' },
    { code: 'ua', labelKey: 'LANGUAGES.UKRAINIAN', flagClass: 'iti__ua' },
  ];

  private readonly supportedLangs = this.languages.map(l => l.code) as ReadonlyArray<SkiworldModels.LangCode>;

  private toLangCode(c: string): SkiworldModels.LangCode | null {
    const norm = c.split('-')[0].toLowerCase();
    return this.supportedLangs.includes(norm as SkiworldModels.LangCode) ? (norm as SkiworldModels.LangCode) : null;
  }

  constructor() {
    const iconReg = inject(MatIconRegistry);
    const sanitizer = inject(DomSanitizer);

    const icons: Record<string, string> = {
      world: 'assets/icons/world.svg',
      location: 'assets/icons/location.svg',
      cart: 'assets/icons/cart.svg',
      login: 'assets/icons/login.svg',
      'flag-ae': 'assets/flags/1x1/ae.svg',
      'flag-cs': 'assets/flags/1x1/cz.svg',
      'flag-de': 'assets/flags/1x1/de.svg',
      'flag-da': 'assets/flags/1x1/dk.svg',
      'flag-es': 'assets/flags/1x1/es.svg',
      'flag-fi': 'assets/flags/1x1/fi.svg',
      'flag-fr': 'assets/flags/1x1/fr.svg',
      'flag-en': 'assets/flags/1x1/gb.svg',
      'flag-hr': 'assets/flags/1x1/hr.svg',
      'flag-hu': 'assets/flags/1x1/hu.svg',
      'flag-il': 'assets/flags/1x1/il.svg',
      'flag-it': 'assets/flags/1x1/it.svg',
      'flag-nl': 'assets/flags/1x1/nl.svg',
      'flag-no': 'assets/flags/1x1/no.svg',
      'flag-pl': 'assets/flags/1x1/pl.svg',
      'flag-ru': 'assets/flags/1x1/ru.svg',
      'flag-se': 'assets/flags/1x1/se.svg',
      'flag-si': 'assets/flags/1x1/si.svg',
      'flag-sk': 'assets/flags/1x1/sk.svg',
      'flag-ua': 'assets/flags/1x1/ua.svg',
    };
    Object.entries(icons).forEach(([n, u]) => iconReg.addSvgIcon(n, sanitizer.bypassSecurityTrustResourceUrl(u)));

    this.createForm();
    this.translate.addLangs(['en', 'de', 'ae', 'cz', 'da', 'es', 'fi', 'fr', 'hr', 'hu', 'il', 'it', 'nl', 'no', 'pl', 'ru', 'se', 'si', 'sk', 'ua']);

    const storedLocale = localStorage.getItem('locale') || '';
    const selLocale = this.lang.normalize(storedLocale, 'de');

    this.translateCode = selLocale.translate;
    this.localeCode = selLocale.locale;

    const browserLang = navigator.languages?.[0] || navigator.language || 'de';
    const initial = this.toLangCode(storedLocale) ?? this.toLangCode(browserLang) ?? 'de';

    //this.translate.setDefaultLang('de');
    this.translate.use(this.translateCode);
    this.locale = initial;

    localStorage.setItem('locale', initial);
    //document.documentElement.lang = initial;
    document.documentElement.lang = this.translateCode;
  }

  private readonly personKeys: Record<SkiworldModels.PersonType, { one: string; other: string }> = {
    ADULT: { one: 'PERSONS.ADULT', other: 'PERSONS.ADULTS' },
    JUNIOR: { one: 'PERSONS.TEENAGER', other: 'PERSONS.TEENAGERS' },
    CHILD: { one: 'PERSONS.CHILD', other: 'PERSONS.CHILDREN' },
  };

  ngOnInit() {
    this.dataService.isAuthenticated$.subscribe((isAuthenticated: boolean) => {
      this.isAuthenticated = isAuthenticated;
    });

    this.dataService.user$.subscribe((user: SkiworldModels.User) => {
      this.username = (user?.username || user?.email) as string;
    });

    this.dataService.headerClass$.subscribe(headerClass => {
      this.headerClass = headerClass;
    });

    this.dataService.duration$.pipe(filter((d): d is SkiworldModels.Duration => d != null)).subscribe(duration => {
      this.rentDuration = formatDate(duration.start, 'EEE, dd.MM.yyyy', this.localeCode) + ' - \r' + formatDate(duration.end, 'EEE, dd.MM.yyyy', this.localeCode);

      this.rentDurationStart = formatDate(duration.start, 'EEE, dd.MM.yyyy', this.localeCode);
      this.rentDurationEnd = formatDate(duration.end, 'EEE, dd.MM.yyyy', this.localeCode);
      let dateFrom = new Date(duration.start);
      let dateTo = new Date(duration.end);
      dateFrom.setHours(0, 0, 0, 0);
      dateTo.setHours(0, 0, 0, 0);
      const diffMs = dateTo.getTime() - dateFrom.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      this.rentDurationDayCount = Math.round(diffDays) + 1;
      this.duration = duration;
      //this.rentDurationDayCount = Math.floor((dayjs(duration.end).toDate().getTime() - dayjs(duration.start).toDate().getTime()) / (1000 * 60 * 60 * 24));
    });

    this.dataService.personsOverview$.subscribe((personsOverview: SkiworldModels.PersonsOverview) => {
      if (personsOverview) {
        this.adultsCount = personsOverview.adults || 0;
        this.juniorsCount = personsOverview.teenagers || 0;
        this.childrenCount = personsOverview.childs || 0;
        this.personCount = this.adultsCount + this.juniorsCount + this.childrenCount;
      }
    });

    this.dataService.persons$.subscribe((persons: SkiworldModels.Person[]) => {
      if (!persons) return;
      this.products = this.getCompleteProductCount() || 0;
      this.persons = persons;
    });

    this.dataService.location$.subscribe((location: SkiworldModels.Locations) => {
      if (location) {
        this.locationName = `${location.name}`;
      } else {
        this.locationName = '-';
      }
    });
  }

  get currentFlagClass(): string {
    const code = (this.locale as string) ?? 'de';
    return this.languages.find(l => l.code === code)?.flagClass ?? 'iti__de';
  }

  getProductCount(activePerson: SkiworldModels.Person): number {
    return activePerson && activePerson.shoppingBasket ? activePerson.shoppingBasket.reduce((total, item) => total + +(item.quantity || 0), 0) : 0;
  }

  getCompleteProductCount(): number {
    return this.persons.reduce((sum, person) => sum + +(this.getProductCount(person) || 0), 0);
  }

  getCompleteOnlinePrice(): number {
    const resDate = new Date();
    const pickupDate = dayjs(this.duration.start).toDate();
    const endDate = dayjs(this.duration.end).toDate();
    return this.persons.reduce((sum, person) => sum + (this.dataService.getCalculatedOnlinePrice(this.persons, person.personId, this.rentDurationDayCount, resDate, pickupDate, endDate) || 0), 0);
  }

  getFinalPrice(): number {
    const originalPrice = this.getCompleteOnlinePrice();
    return originalPrice;
  }

  getTranslation(type: SkiworldModels.PersonType, count: number): string {
    const form: 'one' | 'other' = count === 1 ? 'one' : 'other';
    const key = this.personKeys[type]?.[form];
    return key ? this.translate.instant(key, { count }) : '';
  }

  private createForm(): void {
    this.loginForm = this.fb.group({
      lang: [],
      username: [],
      password: [],
    });
    this.loginForm.enable();

    this.logoutForm = this.fb.group({
      lang: [],
      username: [],
      fullname: [],
    });
    this.logoutForm.disable();
  }

  public onSelect(target: string): void {
    switch (target) {
      default:
        this.router.navigate(['/']).then(() => {
          this.router.navigateByUrl(`${target}`);
        });
    }
  }

  openPage(page: string): void {
    let newRoute;
    const pre = 'rent-';
    switch (page) {
      case 'duration':
      case 'persons':
      case 'location':
      case 'products':
      case 'shopping-basket':
        newRoute = pre + page;
        break;
      default:
        newRoute = page;
        break;
    }
    this.router.navigate([newRoute]);
  }

  switchLanguage(newLanguage: string): void {
    const sel = this.lang.switch(newLanguage, 'de');

    /* this.translateCode = sel.translate;
    this.localeCode = sel.locale;

    this.locale = sel.locale; */
  }

  logout() {
    this.auth.logout();
    //localStorage.clear();
    this.currentUser = {} as SkiworldModels.User;
    //this.router.navigateByUrl('/');
  }

  login() {
    this.auth.initLogin();
  }

  userDetailFromToken() {
    const token = localStorage.getItem('id_token') as string;
    const decodedToken = this.jwtHelper.decodeToken(token);
    const name = decodedToken['username'];
    this.username = name as string;
    console.log('name: ' + name.split(' ')[0]);
    console.log('email: ' + decodedToken['email']);
  }
}
